import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import formatDate from '../../components/formatDate';
import {
  getMissionsPayload,
  listMissions,
  listParticipants,
} from '../../services/missionsApi';
import { PARTICIPANT_STATUSES, SUBJECT_TYPES } from './missionUtils';
import { useMissionsSession } from './useMissionsSession';
import MissionDashboardSummary from './MissionDashboardSummary';

const PAGE_SIZE = 20;

function parseMissionIdParam(raw) {
  if (!raw) return '';
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? String(id) : '';
}

function ParticipantsList() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [participants, setParticipants] = useState([]);
  const [pagination, setPagination] = useState({ limit: PAGE_SIZE, offset: 0, has_more: false });
  const [missions, setMissions] = useState([]);
  const [missionsError, setMissionsError] = useState('');
  const { handleApiResponse } = useMissionsSession(setError);
  const { handleApiResponse: handleMissionsListResponse } = useMissionsSession(setMissionsError);

  const applied = useMemo(() => ({
    search: searchParams.get('search') || '',
    status: searchParams.get('status') || 'all',
    subject_type: searchParams.get('subject_type') || '',
    mission_id: parseMissionIdParam(searchParams.get('mission_id')),
    offset: Math.max(0, Number(searchParams.get('offset') || 0) || 0),
  }), [searchParams]);

  const [search, setSearch] = useState(applied.search);
  const [status, setStatus] = useState(applied.status);
  const [subjectType, setSubjectType] = useState(applied.subject_type);
  const [missionId, setMissionId] = useState(applied.mission_id);

  useEffect(() => {
    setSearch(applied.search);
    setStatus(applied.status);
    setSubjectType(applied.subject_type);
    setMissionId(applied.mission_id);
  }, [applied]);

  const writeParams = useCallback((next) => {
    const params = {};
    if (next.search.trim()) params.search = next.search.trim();
    if (next.status && next.status !== 'all') params.status = next.status;
    if (next.subject_type) params.subject_type = next.subject_type;
    if (next.mission_id) params.mission_id = next.mission_id;
    if (next.offset) params.offset = String(next.offset);
    setSearchParams(params);
  }, [setSearchParams]);

  const loadMissions = useCallback(async () => {
    setMissionsError('');
    const result = await listMissions();
    if (handleMissionsListResponse(result, 'Failed to load missions for filter.')) {
      setMissions([]);
      return;
    }
    const data = getMissionsPayload(result);
    const list = data?.missions || data || [];
    setMissions(Array.isArray(list) ? list : []);
  }, [handleMissionsListResponse]);

  const loadParticipants = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const filters = {
        limit: PAGE_SIZE,
        offset: applied.offset,
        status: applied.status === 'all' ? undefined : applied.status,
        subject_type: applied.subject_type || undefined,
        mission_id: applied.mission_id || undefined,
        search: applied.search.trim() || undefined,
      };

      const result = await listParticipants(filters);
      if (handleApiResponse(result, 'Failed to load participants.')) {
        setParticipants([]);
        return;
      }

      const data = getMissionsPayload(result);
      setParticipants(data?.participants || []);
      setPagination(data?.pagination || { limit: PAGE_SIZE, offset: applied.offset, has_more: false });
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load participants.');
      setParticipants([]);
    } finally {
      setLoading(false);
    }
  }, [applied, handleApiResponse]);

  useEffect(() => {
    loadMissions();
  }, [loadMissions]);

  useEffect(() => {
    loadParticipants();
  }, [loadParticipants]);

  const applyFilters = () => {
    writeParams({
      search,
      status,
      subject_type: subjectType,
      mission_id: missionId,
      offset: 0,
    });
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') applyFilters();
  };

  const participantLink = (p) =>
    `/admin/missions/participants/${p.subject_type}/${encodeURIComponent(p.subject_id)}`;

  return (
    <Box>
      <MissionDashboardSummary />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Participants</Typography>
        <IconButton onClick={loadParticipants} disabled={loading} title="Refresh">
          <RefreshIcon />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="User id, name, username, guest hash"
          sx={{ minWidth: 220 }}
        />
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          {PARTICIPANT_STATUSES.map((s) => (
            <MenuItem key={s.value} value={s.value}>
              {s.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Subject Type"
          value={subjectType}
          onChange={(e) => setSubjectType(e.target.value)}
          sx={{ minWidth: 130 }}
        >
          {SUBJECT_TYPES.map((s) => (
            <MenuItem key={s.value || 'all'} value={s.value}>
              {s.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Mission"
          value={missionId}
          onChange={(e) => setMissionId(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All missions</MenuItem>
          {missions.map((m) => (
            <MenuItem key={m.id} value={String(m.id)}>
              #{m.sort_order} — {m.name}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" startIcon={<SearchIcon />} onClick={applyFilters}>
          Apply
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Guests show no display name (subject_id is the guest hash). Guests may stall on login-only tasks
        (fan journey, comment, post) until they sign in. Status=completed lists subjects with no active
        mission but at least one completed mission.
      </Alert>

      {missionsError && !error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {missionsError}
        </Alert>
      )}

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Subject</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Active Mission</TableCell>
              <TableCell>Progress</TableCell>
              <TableCell>Completed</TableCell>
              <TableCell>Assigned</TableCell>
              <TableCell>Last Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && participants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No participants found.
                </TableCell>
              </TableRow>
            ) : (
              participants.map((p) => (
                <TableRow
                  key={`${p.subject_type}-${p.subject_id}`}
                  hover
                  component={Link}
                  to={participantLink(p)}
                  sx={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {p.display_name || p.subject_id}
                    </Typography>
                    {p.user_id && (
                      <Typography variant="caption" color="text.secondary">
                        User #{p.user_id}
                      </Typography>
                    )}
                    {p.subject_type === 'guest' && p.display_name && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {p.subject_id}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={p.subject_type} />
                  </TableCell>
                  <TableCell>{p.active_mission_name || '—'}</TableCell>
                  <TableCell sx={{ minWidth: 140 }}>
                    {p.active_mission_id ? (
                      <Box>
                        <LinearProgress
                          variant="determinate"
                          value={p.progress_percentage ?? 0}
                          sx={{ mb: 0.5 }}
                        />
                        <Typography variant="caption">
                          {p.completed_tasks}/{p.total_tasks} ({p.progress_percentage ?? 0}%)
                        </Typography>
                      </Box>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>{p.completed_missions_count ?? 0}</TableCell>
                  <TableCell>{p.assigned_at ? formatDate(p.assigned_at) : '—'}</TableCell>
                  <TableCell>{p.last_action_at ? formatDate(p.last_action_at) : '—'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button
          disabled={applied.offset <= 0 || loading}
          onClick={() => writeParams({ ...applied, offset: Math.max(0, applied.offset - PAGE_SIZE) })}
        >
          Previous
        </Button>
        <Typography variant="body2" color="text.secondary">
          Showing {applied.offset + 1}–{applied.offset + participants.length}
        </Typography>
        <Button
          disabled={!pagination.has_more || loading}
          onClick={() => writeParams({ ...applied, offset: applied.offset + PAGE_SIZE })}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}

export default ParticipantsList;
